import {
  faBoxOpen,
  faExclamationTriangle,
  faInfoCircle,
  faPencil,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  ColumnDef,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import AdvanceTable from "components/base/AdvanceTable";
import AdvanceTableFooter from "components/base/AdvanceTableFooter";
import Button from "components/base/Button";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import { useState } from "react";
import { Alert, Card, Modal, Placeholder, Spinner, Table } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useToast } from "providers/ToastProvider";
import { useKits, useRemoverKit } from "hooks/useKits";
import { KitListItem } from "services/kitService";

// ─── Breadcrumb ────────────────────────────────────────────────────────────────

const breadcrumbItems = [
  { label: "Cadastros", to: "/cadastros" },
  { label: "Kits de Produtos", active: true },
];

// ─── Helpers ───────────────────────────────────────────────────────────────────

const formatCurrency = (value: number | null | undefined) =>
  (value ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

// ─── Modal de confirmação de remoção (autocontido) ─────────────────────────────

interface ModalRemoverKitProps {
  kit: KitListItem | null;
  onClose: () => void;
}

const ModalRemoverKit = ({ kit, onClose }: ModalRemoverKitProps) => {
  const removerMutation = useRemoverKit();
  const { showToast } = useToast();

  const handleConfirm = async () => {
    if (!kit) return;
    try {
      await removerMutation.mutateAsync(kit.id);
      showToast("Kit de produtos removido com sucesso", "success");
      onClose();
    } catch {
      // erro tratado pelo onError do hook
    }
  };

  const handleClose = () => {
    removerMutation.reset();
    onClose();
  };

  return (
    <Modal show={!!kit} onHide={handleClose} centered size="lg" className="fade">
      <Modal.Header className="border-0 pb-0">
        <div className="d-flex justify-content-center w-100">
          <div className="rounded-circle bg-danger bg-opacity-10 p-3 mb-3">
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className="text-danger fa-2x"
            />
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="text-center px-4 pt-0">
        {removerMutation.isError && (
          <Alert
            variant="danger"
            className="border border-danger border-opacity-25 bg-danger-subtle mb-3"
          >
            <div className="text-danger-emphasis">
              <strong>Erro:</strong> Não foi possível remover o kit de produtos.
              Tente novamente.
            </div>
          </Alert>
        )}

        <h4 className="mb-3">Confirmar Remoção</h4>
        <p className="mb-3">Você está prestes a remover o kit de produtos:</p>

        <div className="d-flex align-items-center justify-content-center mb-4">
          <div className="border rounded-4 py-3 px-4 bg-body">
            <div className="d-flex align-items-center">
              <div className="rounded-circle bg-secondary bg-opacity-10 p-2 me-3 shadow-sm">
                <FontAwesomeIcon icon={faBoxOpen} className="text-secondary fa-2x" />
              </div>
              <div className="text-start">
                <h5 className="mb-0">{kit?.descricao}</h5>
                <div className="d-flex align-items-center text-muted">
                  <small className="me-2">Código:</small>
                  <span className="badge bg-secondary">#{kit?.codigo}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-body border-start border-4 border-warning rounded p-3 mb-3 text-start">
          <h6 className="d-flex align-items-center mb-2">
            <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-warning" />
            Informações Importantes
          </h6>
          <p className="text-muted small mb-0">
            Ao remover este kit de produtos, ele deixará de estar disponível para
            venda. Esta ação não pode ser desfeita.
          </p>
        </div>
      </Modal.Body>

      <Modal.Footer className="border-0 justify-content-center pt-0">
        <Button
          variant="outline-secondary"
          onClick={handleClose}
          disabled={removerMutation.isPending}
          className="px-4"
        >
          Cancelar
        </Button>
        <Button
          variant="danger"
          onClick={handleConfirm}
          disabled={removerMutation.isPending}
          className="px-4"
        >
          {removerMutation.isPending ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Removendo...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faTrash} className="me-2" />
              Remover Kit
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ─── Colunas da tabela ─────────────────────────────────────────────────────────

const buildColumns = (
  onEditar: (id: string) => void,
  onRemover: (kit: KitListItem) => void
): ColumnDef<KitListItem>[] => [
  {
    accessorKey: "codigo",
    header: "Código",
    cell: ({ getValue }) => (
      <span className="text-body-emphasis">#{getValue<number>()}</span>
    ),
    size: 80,
  },
  {
    accessorKey: "descricao",
    header: "Descrição",
    cell: ({ row: { original } }) => (
      <Link
        to={`/cadastros/kits/${original.id}`}
        className="fw-semibold line-clamp-3"
      >
        {original.descricao}
      </Link>
    ),
    meta: {
      headerProps: { style: { width: 300 }, className: "ps-4" },
      cellProps: { className: "ps-4" },
    },
  },
  {
    accessorKey: "precoKit",
    header: "Preço do Kit",
    cell: ({ getValue }) => (
      <span className="text-body-secondary">{formatCurrency(getValue<number>())}</span>
    ),
    size: 130,
  },
  {
    accessorKey: "qtdComponentes",
    header: "Componentes",
    cell: ({ getValue }) => {
      const qtd = getValue<number>();
      return qtd > 0 ? (
        <span className="badge bg-primary-subtle text-primary rounded-pill">
          {qtd} {qtd === 1 ? "item" : "itens"}
        </span>
      ) : (
        <span className="text-body-tertiary">—</span>
      );
    },
    size: 120,
  },
  {
    accessorKey: "inativo",
    header: "Situação",
    cell: ({ getValue }) =>
      getValue<boolean>() ? (
        <span className="badge bg-secondary-subtle text-body-secondary rounded-pill">
          Inativo
        </span>
      ) : (
        <span className="badge bg-success-subtle text-success rounded-pill">
          Ativo
        </span>
      ),
    size: 100,
  },
  {
    id: "acoes",
    header: "Ações",
    cell: ({ row: { original } }) => (
      <div className="d-flex gap-2">
        <Button
          variant="phoenix-secondary"
          size="sm"
          className="px-2"
          title="Editar"
          onClick={() => onEditar(original.id)}
        >
          <FontAwesomeIcon icon={faPencil} />
        </Button>
        <Button
          variant="phoenix-danger"
          size="sm"
          className="px-2"
          title="Remover"
          onClick={() => onRemover(original)}
        >
          <FontAwesomeIcon icon={faTrash} />
        </Button>
      </div>
    ),
    size: 100,
  },
];

// ─── Página principal ──────────────────────────────────────────────────────────

const Kits = () => {
  const navigate = useNavigate();
  const dataQuery = useKits();
  const [kitParaRemover, setKitParaRemover] = useState<KitListItem | null>(null);

  const columns = buildColumns(
    (id) => navigate(`/cadastros/kits/${id}`),
    (kit) => setKitParaRemover(kit)
  );

  const table = useReactTable({
    data: dataQuery.data ?? [],
    columns,
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
    initialState: { pagination: { pageSize: 10 } },
  });

  if (dataQuery.isLoading) {
    return (
      <div>
        <PageBreadcrumb items={breadcrumbItems} />
        <div className="mb-9">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="mb-0">Kits de Produtos</h2>
            <Placeholder.Button variant="primary" xs={2} className="px-4 py-2" />
          </div>

          <Card className="border-0 shadow-sm">
            <Card.Body className="p-0">
              <div className="mx-n4 px-4 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent">
                <Table className="phoenix-table fs-9 mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: "10%" }}>Código</th>
                      <th>Descrição</th>
                      <th style={{ width: "13%" }}>Preço do Kit</th>
                      <th style={{ width: "12%" }}>Componentes</th>
                      <th style={{ width: "12%" }}>Situação</th>
                      <th style={{ width: "10%" }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: 6 }).map((_, i) => (
                      <tr key={i}>
                        <td><Placeholder animation="wave" as="div"><Placeholder xs={5} /></Placeholder></td>
                        <td><Placeholder animation="wave" as="div"><Placeholder xs={8} /></Placeholder></td>
                        <td><Placeholder animation="wave" as="div"><Placeholder xs={6} /></Placeholder></td>
                        <td><Placeholder animation="wave" as="div"><Placeholder xs={5} /></Placeholder></td>
                        <td><Placeholder animation="wave" as="div"><Placeholder xs={6} /></Placeholder></td>
                        <td><Placeholder animation="wave" as="div"><Placeholder xs={4} /></Placeholder></td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>
    );
  }

  if (dataQuery.isError) {
    return (
      <div className="text-center py-5 text-danger">
        {(dataQuery.error as Error).message}
      </div>
    );
  }

  return (
    <div>
      <PageBreadcrumb items={breadcrumbItems} />
      <div className="mb-9">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="mb-0">Kits de Produtos</h2>
          <Button
            variant="primary"
            as={Link}
            to="/cadastros/kits/novo"
            className="px-4 py-2"
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Novo Kit
          </Button>
        </div>

        {dataQuery.data?.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="text-center py-5">
                <FontAwesomeIcon
                  icon={faBoxOpen}
                  className="text-body-tertiary mb-3"
                  style={{ fontSize: "2.5rem" }}
                />
                <h6 className="text-body-secondary">Nenhum kit cadastrado</h6>
                <p className="small text-body-tertiary mb-0">
                  Clique em "Novo Kit" para criar seu primeiro kit de produtos.
                </p>
              </div>
            </Card.Body>
          </Card>
        ) : (
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-0">
              <AdvanceTableProvider {...table}>
                <div className="mx-n4 px-4 rounded-4 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
                  <AdvanceTable tableProps={{ className: "phoenix-table fs-9" }} />
                  <AdvanceTableFooter pagination showViewAllBtn={false} />
                </div>
              </AdvanceTableProvider>
            </Card.Body>
          </Card>
        )}
      </div>

      <ModalRemoverKit
        kit={kitParaRemover}
        onClose={() => setKitParaRemover(null)}
      />
    </div>
  );
};

export default Kits;
